import { http as atsHttps } from '@/lib/ats-https';
import { http as v1Https } from '@/lib/http';
import * as Yup from 'yup';
import { validatePhoneNumber } from '@kula-ai/components';
import { checkCondition } from './checkCondition';

const constructFieldSchema = (field, yupSchema) => {
  const {
    name,
    isRequired,
    validation,
    label,
    errorMessage,
    optionalValidation,
    isMulti,
    type,
    validationType,
    fields,
    isConditional,
    conditionConfig,
    isPreviewMode,
  } = field || {};

  // Nesting check and recursive calls:
  if (type === 'wrapper' && fields?.length > 0) {
    field.fields.forEach((subField) => {
      constructFieldSchema(subField, yupSchema);
    });
  }

  // Skip validation construction in preview mode
  if (isPreviewMode) {
    return;
  }

  if (isRequired || optionalValidation) {
    let fieldSchema;

    //  conditional field validation
    if (isConditional) {
      // String / Array is validated, Rest to be added as and when needed
      if (isMulti || type === 'upload') {
        fieldSchema =
          validation ||
          Yup.array().test(
            'conditionalArrayCheck',
            errorMessage ||
              (type === 'upload'
                ? 'Please select at least one file'
                : 'Please select at least one option'),
            (value, context) => {
              // validate only if the condition is true
              if (checkCondition(context?.parent, conditionConfig))
                return value?.length >= 1;
              else return true;
            }
          );
      } else if (validationType === 'object') {
        fieldSchema =
          validation ||
          Yup.object().test(
            'conditionalObjectValidation',
            errorMessage || `${label} is required`,
            (value, context) => {
              // validate only if the condition is true
              if (checkCondition(context?.parent, conditionConfig))
                return (
                  typeof value === 'object' &&
                  !Array.isArray(value) &&
                  value !== null
                );
              else return true;
            }
          );
      } else {
        fieldSchema =
          validation ||
          Yup.string().test(
            'conditionalStringCheck',
            `${errorMessage || `${label} is required`}`,
            (value, context) => {
              // validate only if the condition is true
              if (checkCondition(context?.parent, conditionConfig))
                return value?.length >= 1;
              else return true;
            }
          );
      }
    } else if (type === 'phone') {
      // PhoneInput value shape: { countryCode: string, dialCode: string, number: string }
      // Uses validatePhoneNumber from @kula-ai/components for format validation
      fieldSchema =
        validation ||
        Yup.mixed()
          .nullable()
          .test(
            'phoneRequired',
            errorMessage || `${label} is required`,
            (value) => {
              if (!isRequired) return true;
              return (
                value !== null &&
                typeof value === 'object' &&
                value.number?.trim().length > 0
              );
            }
          )
          .test('phoneValid', 'Invalid phone number', (value) => {
            if (!value || !value.number?.trim()) return true;
            const result = validatePhoneNumber(value.number, value.countryCode);
            return result.isValid;
          });
    } else if (validationType === 'object') {
      fieldSchema =
        validation ||
        Yup.object().required(errorMessage || `${label} is required`);
    } else if (isMulti || type === 'upload') {
      // Handle array validation for isMulti
      fieldSchema =
        validation ||
        Yup.array()
          .required(errorMessage || `${label} is required`)
          .test(
            'hasAtLeastOneValue',
            errorMessage ||
              (type === 'upload'
                ? 'Please select at least one file'
                : 'Please select at least one option'),
            (value) => value.length >= 1
          );
    } else {
      // Default to string validation
      fieldSchema =
        validation ||
        Yup.string().required(errorMessage || `${label} is required`);
    }
    yupSchema[name] = fieldSchema;
  }
};

export const constructYupSchema = (fields) => {
  const yupSchema = {};

  fields?.forEach((field) => {
    if (field?.type === 'wrapper' && field?.fields?.length > 0) {
      field.fields.forEach((subField) => {
        constructFieldSchema(subField, yupSchema);
      });
    }
    constructFieldSchema(field, yupSchema);
  });
  return Yup.object().shape(yupSchema);
};

export const validateFormField = (value, field = {}) => {
  const {
    isRequired = false,
    validation,
    label,
    errorMessage,
    optionalValidation,
  } = field;

  if (isRequired && !value) {
    if (validation && value) return validation.validateSync(value);
    else return errorMessage || `${label} is required`;
  }

  if (optionalValidation && value && validation) {
    return validation.validateSync(value);
  }

  return undefined;
};

export const formValidationError = (toast, formik = {}, errorMessage) => {
  const { isValid, isSubmitting, errors = {}, submitCount } = formik;

  const { base } = errors;
  const errorMessageToDisplay =
    base || errorMessage || 'Please fill all the required form fields';

  const isFormInvalid = !(isValid || isSubmitting || !submitCount);

  if (isFormInvalid) {
    toast({
      title: 'Form Submission Error',
      description: errorMessageToDisplay,
      position: 'top',
      status: 'error',
      duration: 2000,
      isClosable: true,
    });
  }
};

export const fetchSelectOptions = async (apiConfig = {}) => {
  const {
    method = 'get',
    endPoint,
    processResponse = (res) => res?.data,
    httpType = 'ats',
    payload = {},
  } = apiConfig;

  let http = atsHttps;
  if (httpType === 'v1') http = v1Https;

  try {
    const response = await http[method](endPoint, payload); // Await the HTTP request
    const options = await processResponse(response); // Process the response
    return options;
  } catch (error) {
    console.error('Error fetching select options:', error);
    throw error; // Re-throw the error for the caller to handle
  }
};
